import OverlayGraphicIcon from '@src/svgs/OverlayGraphicIcon';
import QuoteStartIcon from '@src/svgs/QuoteStartIcon';
import SpeakersIcon from '@src/svgs/SpeakersIcon';
import TimelineCompositionIcon from '@src/svgs/TimelineCompositionIcon';
import TimelineEndCardIcon from '@src/svgs/TimelineEndCardIcon';
import TimelineIntroCardIcon from '@src/svgs/TimelineIntroCardIcon';
import TimelineLogoIcon from '@src/svgs/TimelineLogoIcon';
import TimelinePhotoIcon from '@src/svgs/TimelinePhotoIcon';
import TimelineShapeIcon from '@src/svgs/TimelineShapeIcon';
import React from 'react';
import styled from 'styled-components';

import type {
  TimelinePreview,
  TypedPreviewKind,
} from '@src/utility/timelinePreview';

/** Below this width the label is dropped; the glyph remains. */
export const GRAPHIC_LABEL_MIN_WIDTH = 64;
/** Below this width only the rail and tinted fill remain. */
export const GRAPHIC_GLYPH_MIN_WIDTH = 28;

const RAIL_WIDTH = 3;
const GLYPH_SIZE = 14;
const GAP = 5;
// 16:9 tile in the 30px bar (22px tall).
const THUMBNAIL_WIDTH = 40;
/** Below this width the thumbnail is dropped so the label keeps its room. */
export const GRAPHIC_THUMBNAIL_MIN_WIDTH =
  GRAPHIC_LABEL_MIN_WIDTH + THUMBNAIL_WIDTH + GAP;

// A photo / logo's image is what tells it apart, so it outlasts the glyph,
// which outlasts the label.
/** Below this width a photo / logo drops its image for the glyph. */
export const IMAGE_THUMBNAIL_MIN_WIDTH = RAIL_WIDTH + GAP + THUMBNAIL_WIDTH + 6;
/** Below this width a photo / logo drops the glyph beside its image. */
export const IMAGE_GLYPH_MIN_WIDTH =
  IMAGE_THUMBNAIL_MIN_WIDTH + GAP + GLYPH_SIZE;
/** Below this width a photo / logo drops its label. */
export const IMAGE_LABEL_MIN_WIDTH = GRAPHIC_THUMBNAIL_MIN_WIDTH;

// PLAT-12135 — a logo is shown whole, never cropped: a wider fixed box
// (~2.5:1, so wordmarks read) with the logo contained inside it. One box for
// every logo keeps blocks aligned whatever the logo's own proportions.
const LOGO_THUMBNAIL_WIDTH = 56;
const LOGO_EXTRA_WIDTH = LOGO_THUMBNAIL_WIDTH - THUMBNAIL_WIDTH;
/** Below this width a logo drops its image for the glyph. */
export const LOGO_THUMBNAIL_MIN_WIDTH =
  IMAGE_THUMBNAIL_MIN_WIDTH + LOGO_EXTRA_WIDTH;
/** Below this width a logo drops the glyph beside its image. */
export const LOGO_GLYPH_MIN_WIDTH = IMAGE_GLYPH_MIN_WIDTH + LOGO_EXTRA_WIDTH;
/** Below this width a logo drops its label. */
export const LOGO_LABEL_MIN_WIDTH = IMAGE_LABEL_MIN_WIDTH + LOGO_EXTRA_WIDTH;

interface TimelineGraphicElementProps {
  preview: TimelinePreview;
  /** Rendered block width in px; decides which layers fit. */
  elementWidth: number;
}

const KindGlyph: React.FC<{ kind: TypedPreviewKind }> = ({ kind }) => {
  const size = String(GLYPH_SIZE);
  switch (kind) {
    case 'overlay':
      return <OverlayGraphicIcon width={size} height={size} />;
    case 'introCard':
      return <TimelineIntroCardIcon width={size} height={size} />;
    case 'endCard':
      return <TimelineEndCardIcon width={size} height={size} />;
    case 'nameCard':
      return <SpeakersIcon width={size} height={size} />;
    case 'quoteCard':
      return (
        <QuoteStartIcon width={size} height="12" fillColor="currentColor" />
      );
    case 'shape':
      return <TimelineShapeIcon width={size} strokeColor="currentColor" />;
    case 'photo':
      return <TimelinePhotoIcon width={size} strokeColor="currentColor" />;
    case 'logo':
      return (
        <TimelineLogoIcon
          width={GLYPH_SIZE}
          height={GLYPH_SIZE}
          fill="currentColor"
        />
      );
    case 'graphic':
    default:
      return (
        <TimelineCompositionIcon
          width={size}
          height={size}
          strokeColor="currentColor"
        />
      );
  }
};

/**
 * PLAT-11816 — the typed preview drawn inside a graphics / shape / photo /
 * logo block.
 *
 * Layers, outermost first: a type-colored rail on the left edge, the template
 * thumbnail as a cropped 16:9 tile (a logo: contained whole in a wider box), the kind's glyph, and the template label.
 * The rail lives here rather than on the block so the block's active-state
 * border and corner marker stay exactly as they are for every other element.
 * Layers give way as the block narrows; the rail never does. Photos and logos
 * keep their image longest, then the glyph, then the label.
 */
export const TimelineGraphicElement: React.FC<TimelineGraphicElementProps> = ({
  preview,
  elementWidth,
}) => {
  const isLogo = preview.kind === 'logo';
  const imageFirst =
    !!preview.thumbnailUrl && (preview.kind === 'photo' || isLogo);
  const imageThumbnailMin = isLogo
    ? LOGO_THUMBNAIL_MIN_WIDTH
    : IMAGE_THUMBNAIL_MIN_WIDTH;
  const imageGlyphMin = isLogo ? LOGO_GLYPH_MIN_WIDTH : IMAGE_GLYPH_MIN_WIDTH;
  const imageLabelMin = isLogo ? LOGO_LABEL_MIN_WIDTH : IMAGE_LABEL_MIN_WIDTH;
  const showThumbnail =
    !!preview.thumbnailUrl &&
    elementWidth >=
      (imageFirst ? imageThumbnailMin : GRAPHIC_THUMBNAIL_MIN_WIDTH);
  const showGlyph =
    elementWidth >=
    (imageFirst && showThumbnail ? imageGlyphMin : GRAPHIC_GLYPH_MIN_WIDTH);
  const showLabel =
    elementWidth >= (imageFirst ? imageLabelMin : GRAPHIC_LABEL_MIN_WIDTH);
  return (
    <Container
      data-preview-kind={preview.kind}
      style={{ color: preview.color }}
    >
      <Rail />
      {showThumbnail ? (
        <Thumbnail
          $contain={isLogo}
          src={preview.thumbnailUrl}
          alt=""
          draggable={false}
          loading="lazy"
        />
      ) : null}
      {showGlyph ? (
        <Glyph aria-hidden="true">
          <KindGlyph kind={preview.kind} />
        </Glyph>
      ) : null}
      {showLabel ? <Label>{preview.label}</Label> : null}
    </Container>
  );
};

const Container = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${GAP}px;
  height: 100%;
  width: 100%;
  min-width: 0;
  padding-left: ${RAIL_WIDTH + GAP}px;
  padding-right: 6px;
  box-sizing: border-box;
  pointer-events: none;
  overflow: hidden;
`;

const Rail = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: ${RAIL_WIDTH}px;
  background: currentColor;
`;

const Thumbnail = styled.img<{ $contain: boolean }>`
  flex: 0 0 auto;
  height: calc(100% - 8px);
  width: ${({ $contain }) =>
    $contain ? LOGO_THUMBNAIL_WIDTH : THUMBNAIL_WIDTH}px;
  box-sizing: border-box;
  padding: ${({ $contain }) => ($contain ? '2px 3px' : '0')};
  object-fit: ${({ $contain }) => ($contain ? 'contain' : 'cover')};
  border-radius: 3px;
  background: var(--surface-subtle);
`;

const Glyph = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  width: ${GLYPH_SIZE}px;
  height: ${GLYPH_SIZE}px;
`;

const Label = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  color: var(--text-primary);
  font-family: var(--font-primary);
  font-size: 10px;
  font-weight: var(--font-weight-medium);
  line-height: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
